<script lang="ts" setup>
import type { UIProp } from '~/types/elementsUi'
import type { ResponsiveImageProps } from '~/types/images'

interface ViewWrapperProps {
  image: ResponsiveImageProps
  darkImage?: ResponsiveImageProps
  title?: string
  description?: string
  hideHead?: boolean
  hideFooter?: boolean
  canonical?: string
  ui?: UIProp<'image' | 'title' | 'subtitle'>
}

const props = defineProps<ViewWrapperProps>()

const route = useRoute()
useHead(() => {
  return {
    link: [
      {
        rel: 'canonical',
        href: props.canonical ?? `https://thuisgekookt.nl${route.path}`
      }
    ]
  }
})

const { t, te } = useI18n()

const slots = useSlots()

const computedUi = computed(() =>
  nuxtUiMerge(props.ui, {
    title: !props.description && !slots['description'] && !slots['buttons'] && tw`mb-0!`,
    subtitle: tw`empty:hidden lg:px-8`
  })
)

const hasMobileImage = computed(
  () => typeof props.image.src === 'object' && props.image.src.mobile
)

const $img = useImage()

// A bug in Nuxt/Image forces images with breakpoints to always load the full image too, which is a big
// performance hit. This block of code manually adds preloading, bypassing the bug
const heroSizes = '640px sm:768px md:1024px lg:1280px xl:1536px 2xl:2560px'

function buildHeroPreload(breakpoint: 'desktop' | 'mobile', color: 'light' | 'dark') {
  const source = color === 'dark' ? props.darkImage : props.image
  if (!source) {
    return null
  }

  const sizes = $img.getSizes(getImageSource(source.src, breakpoint), {
    sizes: heroSizes,
    modifiers: { format: 'webp' } // TODO: change to AVIF when swapping over
  })

  const mediaParts: string[] = []
  if (hasMobileImage.value) {
    mediaParts.push(breakpoint === 'mobile' ? '(max-width: 767px)' : '(min-width: 768px)')
  }
  if (props.darkImage) {
    mediaParts.push(`(prefers-color-scheme: ${color})`)
  }

  return {
    rel: 'preload',
    as: 'image',
    fetchpriority: 'high',
    media: mediaParts.length ? mediaParts.join(' and ') : undefined,
    href: sizes.src,
    imagesizes: sizes.sizes,
    imagesrcset: sizes.srcset
  } as const
}

const heroPreloadLinks = computed(() => {
  const breakpoints = hasMobileImage.value
    ? (['desktop', 'mobile'] as const)
    : (['desktop'] as const)
  const colors = props.darkImage ? (['light', 'dark'] as const) : (['light'] as const)

  return breakpoints
    .flatMap((breakpoint) => colors.map((color) => buildHeroPreload(breakpoint, color)))
    .filter((link) => link !== null)
})

useHead(() => ({ link: heroPreloadLinks.value }))
</script>

<template>
  <div id="navbar-app">
    <header>
      <section class="full-width-hero">
        <div class="hero-background">
          <VImage
            class="hero-image"
            :class="[
              computedUi?.image,
              !darkImage && 'dark:brightness-80 dark:contrast-105 dark:saturate-90',
              hasMobileImage && 'hidden md:block'
            ]"
            :image="image"
            :dark="darkImage"
            sizes="640px sm:768px md:1024px lg:1280px xl:1536px 2xl:2560px"
            width="1024px"
            loading-type="eager"
            disable-placeholder
          />
          <VImage
            v-if="hasMobileImage"
            class="hero-image md:hidden"
            :class="[
              computedUi?.image,
              !darkImage && 'dark:brightness-80 dark:contrast-105 dark:saturate-90'
            ]"
            breakpoint-type="mobile"
            :image="image"
            :dark="darkImage"
            sizes="640px sm:768px md:1024px lg:1280px xl:1536px 2xl:2560px"
            width="1024px"
            loading-type="eager"
            disable-placeholder
          />
        </div>
        <div class="hero-head">
          <Navbar v-if="!hideHead" show-transparent-effect />
          <div v-else class="spacer" />
        </div>
        <div class="hero-body">
          <div class="desktop-body hidden xl:block">
            <slot name="desktop-body">
              <div
                class="flex max-w-[min(33vw,660px)] flex-col items-center rounded-2xl bg-primary-100/85 py-8 backdrop-blur-xs dark:bg-pure-black/90"
              >
                <h1
                  class="title w-[95%] px-1 text-center text-h1 wrap-anywhere text-primary-700 dark:text-beige"
                  :class="computedUi?.title"
                >
                  <slot name="title">
                    {{ title && te(title) ? t(title) : title }}
                  </slot>
                </h1>

                <div :class="computedUi?.subtitle">
                  <slot name="subtitle" />
                </div>

                <p class="px-8 text-xl empty:hidden">
                  <slot name="description">
                    {{ description && te(description) ? t(description) : description }}
                  </slot>
                </p>
                <div
                  class="mt-6 flex w-full flex-wrap gap-2 px-8 *:min-w-fit *:flex-initial *:grow empty:hidden"
                >
                  <slot name="buttons" />
                </div>
              </div>
            </slot>
          </div>
          <div class="flex justify-center xl:hidden">
            <slot name="mobile-body">
              <div class="flex h-full max-w-120 grow flex-col justify-end gap-8">
                <h1
                  class="title mb-0! text-h1 wrap-anywhere text-primary-700 md:text-center dark:text-beige"
                  :class="[
                    !$slots['buttons'] && !$slots['subtitle'] && 'mb-20!',
                    computedUi?.title
                  ]"
                >
                  {{ title && te(title) ? t(title) : title }}
                </h1>

                <div :class="computedUi?.subtitle">
                  <slot name="subtitle" />
                </div>

                <div class="mb-6 flex flex-col gap-2 empty:hidden">
                  <slot name="buttons" />
                </div>
              </div>
            </slot>
          </div>
        </div>
      </section>
    </header>

    <main>
      <slot />
    </main>

    <Footer v-if="!hideFooter" />
  </div>
</template>

<style scoped></style>
